import VideoProvider from 'VideoProvider';
import { redirectTo } from 'modules/auth/actions/redirectAction';
import { clearModalQueue } from 'modules/messageSystem/actions/clearModalQueue';
import {
  conferenceStopping,
  conferenceInProgress,
  isNeedToShowLocalVideo,
} from 'modules/conference/selectors/consultationInfo';

import createAction from './';

export const GET_LOCAL_MEDIA_STREAM = createAction('GET_LOCAL_MEDIA_STREAM');
export const GET_REMOTE_MEDIA_STREAM = createAction('GET_REMOTE_MEDIA_STREAM');
export const CREATE_PROVIDER = createAction('CREATE_PROVIDER');
export const START_CALL = createAction('START_CALL');
export const STOP_CALL = createAction('STOP_CALL');
export const STOP_CALL_RECONNECTION_FAILED = createAction('STOP_CALL_RECONNECTION_FAILED');
export const BROADCAST_RECONNECT = createAction('BROADCAST_RECONNECT');
export const BROADCAST_TOGGLE_AUDIO_MUTE = createAction('BROADCAST_TOGGLE_AUDIO_MUTE');
export const BROADCAST_TOGGLE_VIDEO_MUTE = createAction('BROADCAST_TOGGLE_VIDEO_MUTE');
export const SET_SOUND_LEVEL = createAction('SET_SOUND_LEVEL');
export const SET_PREV_SOUND_LEVEL = createAction('SET_PREV_SOUND_LEVEL');

let videoProvider;
let selfMediaStream;

export const initProvider = (options) => (dispatch) => {
  videoProvider = new VideoProvider(options);

  dispatch({
    type: CREATE_PROVIDER,
    payload: { videoProvider },
  });

  return Promise.resolve(videoProvider);
};

export const startCall = (jwtToken) => (dispatch) => {
  if (!videoProvider) return Promise.resolve();

  dispatch({
    type: START_CALL,
    payload: { videoProvider },
  });

  return videoProvider
    .startMedia()
    .then(() => videoProvider.register(jwtToken));
};

const broadcastEndConsultation = () =>
  videoProvider.broadcast({ consultationEnd: true })
    .catch(() => console.log('fail broadcast'));

export const stopCall = () => (dispatch) => {
  selfMediaStream = null;

  dispatch({
    type: STOP_CALL,
    payload: { videoProvider },
  });

  if (!videoProvider) return Promise.resolve();

  return Promise.resolve()
    .then(() => broadcastEndConsultation())
    .then(() => videoProvider.release());
};

export const stopCallReconnectionFailed = () => (dispatch) => {
  selfMediaStream = null;

  dispatch({
    type: STOP_CALL_RECONNECTION_FAILED,
    payload: { videoProvider },
  });

  if (!videoProvider) return Promise.resolve();

  return Promise.resolve()
    .then(() => {
      if (!navigator.onLine) {
        localStorage.setItem('offline_started', new Date().toString());

        dispatch(clearModalQueue());
        // return redirectTo('/noInternet');
      }
    });
};

export const getLocalMediaStream = () => (dispatch, getState) => {
  const state = getState();
  if (!isNeedToShowLocalVideo(state)) return Promise.resolve();
  if (selfMediaStream) return Promise.resolve(selfMediaStream);

  return navigator.mediaDevices?.getUserMedia({ video: true })
    .then((stream) => {
      stream.getVideoTracks()[0].enabled = !(state.conference.consultationInfo.selfVideoOff);

      selfMediaStream = stream;
      dispatch({
        type: GET_LOCAL_MEDIA_STREAM,
        payload: { stream },
      });

      return stream;
    });
};

export const getRemoteMediaStream = () => (dispatch, getState) => {
  if (!videoProvider || conferenceStopping(getState())) return Promise.resolve();

  return videoProvider
    .getRemoteMediaStream()
    .then(stream => {
      dispatch({
        type: GET_REMOTE_MEDIA_STREAM,
        payload: { stream },
      });

      return stream;
    });
};

export const getVideoMediaStream = (isLocalVideo) => (dispatch, getState) => {
  const state = getState();
  const { switchedLayout } = state.conference.consultationInfo;
  let getStream;

  if (isLocalVideo) {
    getStream = switchedLayout ? getRemoteMediaStream : getLocalMediaStream;
  } else {
    getStream = switchedLayout ? getLocalMediaStream : getRemoteMediaStream;
  }

  return dispatch(getStream());
};

export const reconnect = () => (dispatch) => {
  dispatch({ type: BROADCAST_RECONNECT });
  return videoProvider.broadcast({ reconnect: true });
};

export const toggleAudioMute = () => (dispatch) => {
  if (!videoProvider) return Promise.resolve();

  return videoProvider
    .toggleAudioMute()
    .then((audioDisabled) =>
      dispatch({
        type: BROADCAST_TOGGLE_AUDIO_MUTE,
        meta: { audioDisabled },
      }),
    );
};

export const toggleVideoMute = (isMuted) => (dispatch) => {
  if (selfMediaStream) selfMediaStream.getVideoTracks()[0].enabled = !isMuted;

  if (!videoProvider) return Promise.resolve();

  return videoProvider
    .toggleVideoMute()
    .then((videoDisabled) =>
      dispatch({
        type: BROADCAST_TOGGLE_VIDEO_MUTE,
        meta: { videoDisabled },
      }),
    );
};

export const setSoundLevel = (value) => (dispatch) => {
  dispatch({
    type: SET_SOUND_LEVEL,
    meta: { value },
  });

  if (!videoProvider) return;

  return videoProvider
    .setVolumeLevel(value / 100);
};

export const setPrevSoundLevel = (value) => ({
  type: SET_PREV_SOUND_LEVEL,
  payload: { value },
});
